import { Fragment, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { PencilSimpleLine, Sparkle } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { isAllowedToView } from "../../../../utils/AuthUtils";
import { permissions } from "../../../../config/paths";
import MODULES from "../../../../config/modules";
import { AI_INGEST_TARGETS } from "../../aiIngestTarget";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import AiMappingModal from "../../../aiDataMapping/components/AiMappingModal";
import {
  scopeTwoV2Keys,
  useAddOnsiteGeneration,
  useAddPurchasedElectricity,
  useDeleteOnsiteGeneration,
  useDeletePurchasedElectricity,
  useGetOnsiteGeneration,
  useGetPurchasedElectricity,
  useUpdateOnsiteGeneration,
  useUpdatePurchasedElectricity,
} from "../../../../hooks/query/questionnaire/scope-two-v2";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { resolveKwhMeasureId } from "./helpers";
import PurchasedElectricityTable from "./PurchasedElectricityTable";
import PurchasedElectricityModal from "./PurchasedElectricityModal";
import OnsiteGenerationTable from "./OnsiteGenerationTable";
import OnsiteGenerationModal from "./OnsiteGenerationModal";

// Full-dataset fetch (matches the rest of the questionnaire grids — size 9999,
// id,desc). v2 has no client-side pagination yet so one page holds every row.
const FETCH_QUERY_PARAMS = { page: 0, size: 9999, sort: "id,desc" };

/**
 * Scope 2 v2 (categorized) data-entry view. Replaces the v1 electricity grid
 * with two accordion sections — Purchased Electricity and On-site Generation —
 * each backed by its own table + add/edit modal. The methodology & sources
 * panel renders after the sections. Props mirror the sibling IndirectEmissions
 * so the branch point in IndirectEmissions.jsx can swap one for the other.
 */
function IndirectEmissionsV2({
  organizationStructure,
  defaultValues,
  isPreview,
  dataType,
  methodologyAndSourcesPanel,
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });

  const questionnaireId = defaultValues?.id;
  const dimensionItems = defaultValues?.dimensionItems ?? null;
  const kwhMeasureId = resolveKwhMeasureId(organizationStructure);

  // Same gate as the route itself (paths.js), so the button never leads somewhere that refuses.
  const { userPermissions } = useAuth();
  const canUseAiMapping = !!questionnaireId
    && isAllowedToView(userPermissions, permissions.aiDataMapping, [MODULES.DataMapping.id]);

  // One shared overlay for both v2 sections (purchased electricity, onsite generation) — `renderAddPill`
  // is called once per section with its own target, so the target is tracked separately from isOpen.
  const [aiMappingTarget, setAiMappingTarget] = useState(null);
  const {
    isOpen: isAiMappingOpen,
    onOpen: onAiMappingOpen,
    onClose: onAiMappingClose,
  } = useDisclosure();

  // ---- modal + delete-dialog state (declare all hooks before any return) ----
  const [purchasedModal, setPurchasedModal] = useState({ open: false, editData: null });
  const [onsiteModal, setOnsiteModal] = useState({ open: false, editData: null });
  const [deleteTarget, setDeleteTarget] = useState(null); // { section, record }

  const queryEnabled = !!questionnaireId && !!organizationStructure;

  const { data: purchasedPage } = useGetPurchasedElectricity(
    { questionnaireId, queryParams: FETCH_QUERY_PARAMS },
    {
      enabled: queryEnabled,
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );
  const { data: onsitePage } = useGetOnsiteGeneration(
    { questionnaireId, queryParams: FETCH_QUERY_PARAMS },
    {
      enabled: queryEnabled,
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    }
  );

  const purchasedRows = purchasedPage?.content ?? [];
  const onsiteRows = onsitePage?.content ?? [];

  const invalidatePurchased = () =>
    queryClient.invalidateQueries(scopeTwoV2Keys.purchasedElectricity());
  const invalidateOnsite = () =>
    queryClient.invalidateQueries(scopeTwoV2Keys.onsiteGeneration());

  const onMutationError = (err) =>
    showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);

  // ---- purchased mutations ----
  const { mutate: addPurchased } = useAddPurchasedElectricity({
    onSuccess: () => {
      setPurchasedModal({ open: false, editData: null });
      showSuccessToast(toast, t("added", "Added"));
      invalidatePurchased();
    },
    onError: onMutationError,
  });
  const { mutate: updatePurchased } = useUpdatePurchasedElectricity({
    onSuccess: () => {
      setPurchasedModal({ open: false, editData: null });
      showSuccessToast(toast, t("updated", "Updated"));
      invalidatePurchased();
    },
    onError: onMutationError,
  });
  const { mutate: deletePurchased } = useDeletePurchasedElectricity({
    onSuccess: () => {
      setDeleteTarget(null);
      showSuccessToast(toast, t("deleted", "Deleted"));
      invalidatePurchased();
    },
    onError: onMutationError,
  });

  // ---- onsite mutations ----
  const { mutate: addOnsite } = useAddOnsiteGeneration({
    onSuccess: () => {
      setOnsiteModal({ open: false, editData: null });
      showSuccessToast(toast, t("added", "Added"));
      invalidateOnsite();
    },
    onError: onMutationError,
  });
  const { mutate: updateOnsite } = useUpdateOnsiteGeneration({
    onSuccess: () => {
      setOnsiteModal({ open: false, editData: null });
      showSuccessToast(toast, t("updated", "Updated"));
      invalidateOnsite();
    },
    onError: onMutationError,
  });
  const { mutate: deleteOnsite } = useDeleteOnsiteGeneration({
    onSuccess: () => {
      setDeleteTarget(null);
      showSuccessToast(toast, t("deleted", "Deleted"));
      invalidateOnsite();
    },
    onError: onMutationError,
  });

  // ---- submit handlers (data already mapped to DTO array by the modals) ----
  const handlePurchasedSubmit = (apiData, isEdit) => {
    if (isEdit) {
      updatePurchased({ questionnaireId, recordId: apiData[0].id, data: apiData[0] });
    } else {
      addPurchased({ questionnaireId, data: apiData });
    }
  };
  const handleOnsiteSubmit = (apiData, isEdit) => {
    if (isEdit) {
      updateOnsite({ questionnaireId, recordId: apiData[0].id, data: apiData[0] });
    } else {
      addOnsite({ questionnaireId, data: apiData });
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    if (deleteTarget.section === "purchased") {
      deletePurchased({ questionnaireId, recordId: deleteTarget.record.id });
    } else {
      deleteOnsite({ questionnaireId, recordId: deleteTarget.record.id });
    }
  };

  const emptyLabel = (sectionTitle) =>
    t("noDataYet", {
      defaultValue: `No ${dataType === "emissions" ? "emissions" : "activity"} data added yet for ${sectionTitle}.`,
      dataType: dataType === "emissions" ? "emissions" : "activity",
      section: sectionTitle,
    });

  /**
   * The add pill, optionally followed by an AI Mapping deep link for `aiTarget`.
   *
   * The two v2 sections have their own ingest targets, but the tables themselves never receive
   * `questionnaireId` — the parent does — so the button belongs here rather than inside them.
   */
  const renderAddPill = (label, onClick, aiTarget) => (
    <Flex gap={2} mb={4} align="center" flexWrap="wrap">
      <Button
        size="sm"
        variant="outline"
        borderRadius="full"
        borderColor="lime"
        bg="lightLime"
        color="limeShade.600"
        leftIcon={<PencilSimpleLine size={14} />}
        onClick={onClick}
        _hover={{ bg: "lightLime", borderColor: "lime" }}
      >
        {label}
      </Button>
      {aiTarget && canUseAiMapping && (
        <Button
          size="sm"
          variant="outline"
          borderRadius="full"
          borderColor="limeShade.600"
          bg="transparent"
          color="limeShade.600"
          leftIcon={<Sparkle size={14} />}
          onClick={() => {
            setAiMappingTarget(aiTarget);
            onAiMappingOpen();
          }}
          _hover={{ bg: "lightLime", borderColor: "lime" }}
        >
          AI Mapping
        </Button>
      )}
    </Flex>
  );

  return (
    <Fragment>
      <Box marginBlockStart="10" overflow="auto">
        {queryEnabled && (
          <Accordion allowMultiple defaultIndex={[0, 1]}>
            {/* ---- Purchased electricity ---- */}
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton px={0} pt={4} pb={4} _hover={{ bg: "transparent" }}>
                <Text
                  as="h3"
                  fontSize="13px"
                  fontWeight="700"
                  color="gray.500"
                  textAlign="left"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {t("sections.purchasedElectricity")}
                </Text>
                <AccordionIcon mr={0} ml="auto" boxSize={6} color="gray.500" />
              </AccordionButton>
              <AccordionPanel px={0} pb={6}>
                {!isPreview && renderAddPill(t("addPurchased"), () =>
                  setPurchasedModal({ open: true, editData: null }),
                  AI_INGEST_TARGETS.SCOPE_2_V2_PURCHASED_ELECTRICITY
                )}
                {purchasedRows.length === 0 ? (
                  <Box border="1px dashed" borderColor="gray.300" borderRadius="md" py={10} textAlign="center" mb={2}>
                    <Text fontSize="14px" color="gray.400">
                      {emptyLabel(t("sections.purchasedElectricity"))}
                    </Text>
                  </Box>
                ) : (
                  <PurchasedElectricityTable
                    organizationStructure={organizationStructure}
                    rows={purchasedRows}
                    isPreview={isPreview}
                    onEdit={(record) => setPurchasedModal({ open: true, editData: record })}
                    onDelete={(record) => setDeleteTarget({ section: "purchased", record })}
                  />
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* ---- On-site generation ---- */}
            <AccordionItem border="none" borderTop="1px solid" borderColor="gray.200">
              <AccordionButton px={0} pt={4} pb={4} _hover={{ bg: "transparent" }}>
                <Text
                  as="h3"
                  fontSize="13px"
                  fontWeight="700"
                  color="gray.500"
                  textAlign="left"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  {t("sections.onsiteGeneration")}
                </Text>
                <AccordionIcon mr={0} ml="auto" boxSize={6} color="gray.500" />
              </AccordionButton>
              <AccordionPanel px={0} pb={6}>
                {!isPreview && renderAddPill(t("addOnsite"), () =>
                  setOnsiteModal({ open: true, editData: null }),
                  AI_INGEST_TARGETS.SCOPE_2_V2_ONSITE_GENERATION
                )}
                {onsiteRows.length === 0 ? (
                  <Box border="1px dashed" borderColor="gray.300" borderRadius="md" py={10} textAlign="center" mb={2}>
                    <Text fontSize="14px" color="gray.400">
                      {emptyLabel(t("sections.onsiteGeneration"))}
                    </Text>
                  </Box>
                ) : (
                  <OnsiteGenerationTable
                    organizationStructure={organizationStructure}
                    rows={onsiteRows}
                    isPreview={isPreview}
                    onEdit={(record) => setOnsiteModal({ open: true, editData: record })}
                    onDelete={(record) => setDeleteTarget({ section: "onsite", record })}
                  />
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </Box>

      {methodologyAndSourcesPanel}

      {purchasedModal.open && (
        <PurchasedElectricityModal
          isOpen={purchasedModal.open}
          onClose={() => setPurchasedModal({ open: false, editData: null })}
          onSubmitPurchased={handlePurchasedSubmit}
          organizationStructure={organizationStructure}
          dimensionItems={dimensionItems}
          editData={purchasedModal.editData}
          kwhMeasureId={kwhMeasureId}
        />
      )}
      {onsiteModal.open && (
        <OnsiteGenerationModal
          isOpen={onsiteModal.open}
          onClose={() => setOnsiteModal({ open: false, editData: null })}
          onSubmitOnsite={handleOnsiteSubmit}
          organizationStructure={organizationStructure}
          dimensionItems={dimensionItems}
          editData={onsiteModal.editData}
        />
      )}
      <DeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDelete={handleDeleteConfirm}
        entityName={"Item"}
      />
      {canUseAiMapping && (
        <AiMappingModal
          isOpen={isAiMappingOpen}
          onClose={onAiMappingClose}
          questionnaireId={questionnaireId}
          target={aiMappingTarget}
        />
      )}
    </Fragment>
  );
}

export default IndirectEmissionsV2;
